import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export type SettingGroup = 'validated' | 'pediatric' | 'adult';

export interface SettingOption {
  id: string;
  displayName: string;
  group: SettingGroup;
}

interface SettingComboboxProps {
  value: string;
  options: SettingOption[];
  onChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const GROUP_LABELS: Record<SettingGroup, string> = {
  validated: 'Validated',
  pediatric: 'Pediatric',
  adult: 'Adult',
};

const GROUP_ORDER: SettingGroup[] = ['validated', 'pediatric', 'adult'];

export function SettingCombobox({
  value,
  options,
  onChange,
  placeholder = 'Select a setting…',
  disabled,
  className,
}: SettingComboboxProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.id === value);

  const grouped: Record<SettingGroup, SettingOption[]> = {
    validated: [], pediatric: [], adult: [],
  };
  options.forEach((o) => grouped[o.group].push(o));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          {selected ? selected.displayName : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <Command>
          <CommandInput placeholder="Search settings…" />
          <CommandList>
            <CommandEmpty>No setting found.</CommandEmpty>
            {GROUP_ORDER.map((group) =>
              grouped[group].length > 0 ? (
                <CommandGroup key={group} heading={GROUP_LABELS[group]}>
                  {grouped[group].map((opt) => (
                    <CommandItem
                      key={opt.id}
                      value={opt.displayName}
                      onSelect={() => {
                        onChange(opt.id);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn('mr-2 h-4 w-4',
                          value === opt.id ? 'opacity-100' : 'opacity-0')}
                      />
                      {opt.displayName}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
